import { Link } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import { displayDate } from "src/utils/dateHandler.util";
import {
  IntegrationHealthException,
  IntegrationHealthProvider,
} from "../types/integration-health.entity";

type Props = {
  data: IntegrationHealthException[];
  isDarkMode: boolean;
};

const providerLabels: Record<IntegrationHealthProvider, string> = {
  FCM: "FCM",
  CUSTOMER_IO: "Customer.io",
  STRIPE: "Stripe",
  AGORA: "Agora",
  TREMENDOUS: "Tremendous",
  AZURE_OPENAI: "Azure OpenAI",
};

const formatOperation = (operation: string) =>
  operation.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");

const providerClass = (
  provider: IntegrationHealthProvider,
  isDarkMode: boolean,
) => {
  const colors: Record<IntegrationHealthProvider, string> = isDarkMode
    ? {
        FCM: "border-orange-400/30 bg-orange-400/10 text-orange-100",
        CUSTOMER_IO: "border-teal-400/30 bg-teal-400/10 text-teal-100",
        STRIPE: "border-violet-400/30 bg-violet-400/10 text-violet-100",
        AGORA: "border-cyan-400/30 bg-cyan-400/10 text-cyan-100",
        TREMENDOUS: "border-amber-400/30 bg-amber-400/10 text-amber-100",
        AZURE_OPENAI: "border-sky-400/30 bg-sky-400/10 text-sky-100",
      }
    : {
        FCM: "border-orange-200 bg-orange-50 text-orange-800",
        CUSTOMER_IO: "border-teal-200 bg-teal-50 text-teal-800",
        STRIPE: "border-violet-200 bg-violet-50 text-violet-800",
        AGORA: "border-cyan-200 bg-cyan-50 text-cyan-800",
        TREMENDOUS: "border-amber-200 bg-amber-50 text-amber-800",
        AZURE_OPENAI: "border-sky-200 bg-sky-50 text-sky-800",
      };

  return colors[provider];
};

const RelatedEntity = ({
  exception,
  isDarkMode,
}: {
  exception: IntegrationHealthException;
  isDarkMode: boolean;
}) => {
  const entity = exception.relatedEntity;
  if (!entity)
    return (
      <span className={isDarkMode ? "text-slate-400" : "text-slate-500"}>
        Global
      </span>
    );

  const content = (
    <>
      <span className="block font-medium capitalize">{entity.type}</span>
      <span
        className={`block max-w-[180px] truncate font-mono text-[11px] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}
      >
        {entity.id}
      </span>
    </>
  );

  if (entity.type !== "user")
    return (
      <div className={isDarkMode ? "text-slate-100" : "text-slate-800"}>
        {content}
      </div>
    );

  return (
    <Link
      to={`/users/${entity.id}`}
      className={`block max-w-[200px] no-underline ${isDarkMode ? "text-sky-300 hover:text-sky-200" : "text-sky-700 hover:text-sky-900"}`}
    >
      {content}
    </Link>
  );
};

export const IntegrationHealthTable = ({ data, isDarkMode }: Props) => (
  <div
    className={`overflow-x-auto border ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}
  >
    <table className="min-w-[1180px] w-full divide-y divide-slate-200 text-left text-sm dark:divide-slate-700">
      <thead
        className={
          isDarkMode ? "bg-slate-800 text-slate-200" : "bg-slate-900 text-white"
        }
      >
        <tr className="text-xs uppercase tracking-wide">
          <th className="px-4 py-3 font-semibold">Provider</th>
          <th className="px-4 py-3 font-semibold">Operation</th>
          <th className="px-4 py-3 font-semibold">Context</th>
          <th className="px-4 py-3 font-semibold">Last error</th>
          <th className="px-4 py-3 font-semibold">First detected</th>
          <th className="px-4 py-3 font-semibold">Last failed</th>
          <th className="px-4 py-3 font-semibold text-right">Attempts</th>
          <th className="px-4 py-3 font-semibold text-right">Open</th>
        </tr>
      </thead>
      <tbody
        className={
          isDarkMode ? "divide-y divide-slate-800" : "divide-y divide-slate-100"
        }
      >
        {data.map((exception) => (
          <tr
            key={exception.id}
            className={
              isDarkMode
                ? "bg-slate-900/50 hover:bg-slate-800/80"
                : "bg-white hover:bg-slate-50"
            }
          >
            <td className="px-4 py-4 align-top">
              <span
                className={`inline-flex rounded border px-2 py-1 text-xs font-medium ${providerClass(exception.provider, isDarkMode)}`}
              >
                {providerLabels[exception.provider]}
              </span>
            </td>
            <td className="px-4 py-4 align-top">
              <span
                className={`block max-w-[180px] capitalize ${isDarkMode ? "text-slate-100" : "text-slate-800"}`}
              >
                {formatOperation(exception.operation)}
              </span>
              <span
                className={`block max-w-[180px] truncate font-mono text-[11px] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}
              >
                {exception.operation}
              </span>
            </td>
            <td className="px-4 py-4 align-top">
              <RelatedEntity exception={exception} isDarkMode={isDarkMode} />
            </td>
            <td className="px-4 py-4 align-top">
              {exception.errorCode && (
                <span
                  className={`mb-1 inline-flex rounded border px-1.5 py-0.5 font-mono text-[11px] ${isDarkMode ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"}`}
                >
                  {exception.errorCode}
                </span>
              )}
              <p
                className={`max-w-[320px] whitespace-pre-wrap break-words ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}
              >
                {exception.errorMessage}
              </p>
            </td>
            <td
              className={`px-4 py-4 align-top whitespace-nowrap ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}
            >
              {displayDate(exception.firstFailedAt)}
            </td>
            <td
              className={`px-4 py-4 align-top whitespace-nowrap ${isDarkMode ? "text-slate-100" : "text-slate-800"}`}
            >
              {displayDate(exception.lastFailedAt)}
            </td>
            <td
              className={`px-4 py-4 text-right align-top font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-800"}`}
            >
              {exception.attemptCount}
            </td>
            <td className="px-4 py-4 text-right align-top">
              {exception.relatedEntity?.type === "user" ? (
                <Link
                  to={`/users/${exception.relatedEntity.id}`}
                  title="Open user"
                  aria-label="Open user"
                  className={`inline-flex h-9 w-9 items-center justify-center rounded border transition-colors ${
                    isDarkMode
                      ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15 hover:text-sky-200"
                      : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
                  }`}
                >
                  <FaExternalLinkAlt className="text-sm" />
                </Link>
              ) : (
                <span
                  className={isDarkMode ? "text-slate-600" : "text-slate-300"}
                >
                  -
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
